import { useCallback, useEffect, useState } from 'react';
import type { ZodError } from 'zod';

import { GLOBAL_API_BASE_URL, VERIFY_API_BASE_URL } from '@chk/env-variables';
import type { GlobalApiPaymentDetails } from '@data/global-api-payments';
import { GlobalApiPaymentsDetailsSchema } from '@data/global-api-payments';
import {
  captureException,
  CURRENT_SERVICE_TYPE,
  usePolling,
} from '@utility/checkout-utils';

import {
  AIS_PATH,
  getScaHeaders,
  SCA_API_ENDPOINTS,
} from '../../global.constants';

type UsePollPaymentDetailsParams = {
  paymentId: string;
  authToken: string;
  skip?: boolean;
  handleResponse: (response: GlobalApiPaymentDetails | null) => void;
};

export const usePollPaymentDetails = ({
  paymentId,
  authToken,
  skip,
  handleResponse,
}: UsePollPaymentDetailsParams) => {
  const [validationError, setValidationError] = useState<ZodError | null>(null);

  const serviceBaseUrl =
    CURRENT_SERVICE_TYPE === AIS_PATH
      ? VERIFY_API_BASE_URL
      : GLOBAL_API_BASE_URL;

  const { startPolling, stopPolling, error } =
    usePolling<GlobalApiPaymentDetails>(
      SCA_API_ENDPOINTS.getPaymentDetails(paymentId, CURRENT_SERVICE_TYPE),
      undefined,
      serviceBaseUrl
    );

  const validateAndHandleResponse = useCallback(
    (result: unknown) => {
      const validationResult = GlobalApiPaymentsDetailsSchema.safeParse(result);

      if (!validationResult.success) {
        setValidationError(validationResult.error);
        captureException(validationResult.error);
        return void handleResponse(null);
      }

      return void handleResponse(validationResult.data);
    },
    [handleResponse]
  );

  useEffect(() => {
    if (!skip) {
      startPolling({
        handleResponse: validateAndHandleResponse,
        requestOptions: {
          headers: getScaHeaders({ authToken }),
        },
      });
    }

    return stopPolling;
  }, [
    skip,
    handleResponse,
    startPolling,
    stopPolling,
    authToken,
    validateAndHandleResponse,
  ]);

  return {
    isError: Boolean(error || validationError),
    error: error ?? validationError,
    isApiError: Boolean(error),
    apiError: error,
  };
};
